// src/features/auth/find-id/pages/FindIdPage.tsx

import React, {
  useState,
} from 'react';

import {
  ArrowLeft,
  CheckCircle2,
  Smartphone,
} from 'lucide-react';

import {
  useNavigate,
} from 'react-router-dom';

import RouteConfig
  from '../../../../routes/RouteConfig';

import {
  useModal,
} from '../../../../shared/components/ModalProvider';

import {
  usePhoneVerification,
} from '../../phone-verification/hooks/usePhoneVerification';

import {
  findId,
} from '../services/findIdService';


const FindIdPage:
  React.FC = () => {
  const navigate =
    useNavigate();

  const modal =
    useModal();


  const [
    phoneNumber,
    setPhoneNumber,
  ] =
    useState('');


  const [
    foundUserId,
    setFoundUserId,
  ] =
    useState<string | null>(
      null,
    );


  const [
    finding,
    setFinding,
  ] =
    useState(false);


  /**
   * =========================================================
   * 아이디 찾기용 전화번호 인증
   * =========================================================
   */
  const phoneVerification =
    usePhoneVerification({
      phoneNumber,

      purpose:
        'FIND_ID',
    });


  /**
   * =========================================================
   * 전화번호 변경
   * =========================================================
   */
  const handlePhoneNumberChange = (
    value: string,
  ): void => {
    const normalized =
      value
        .replace(
          /\D/g,
          '',
        )
        .slice(
          0,
          11,
        );


    /**
     * 전화번호 변경 시
     * 기존 인증 상태 무효화
     */
    phoneVerification
      .resetVerification();


    setFoundUserId(
      null,
    );


    setPhoneNumber(
      normalized,
    );
  };


  /**
   * =========================================================
   * 아이디 조회
   *
   * POST /api/v2/auth/find-id
   *
   * {
   *   phoneNumber,
   *   verificationToken
   * }
   * =========================================================
   */
  const handleFindId =
    async (): Promise<void> => {
      if (
        finding
      ) {
        return;
      }


      if (
        !phoneVerification
          .phoneVerified
      ) {
        await modal.alert(
          '전화번호 인증을 먼저 완료해주세요.',
        );

        return;
      }


      const verificationToken =
        phoneVerification
          .verificationToken;


      if (
        !verificationToken
      ) {
        await modal.alert(
          '인증 정보가 없습니다. 다시 인증해주세요.',
        );

        return;
      }


      const normalizedPhoneNumber =
        phoneNumber
          .replace(
            /\D/g,
            '',
          );


      if (
        !normalizedPhoneNumber
      ) {
        await modal.alert(
          '전화번호를 입력해주세요.',
        );

        return;
      }


      try {
        setFinding(
          true,
        );


        const response =
          await findId({
            phoneNumber:
              normalizedPhoneNumber,

            verificationToken,
          });


        setFoundUserId(
          response.userId,
        );

      } catch (
        error: unknown
      ) {
        console.error(
          '아이디 찾기 실패:',
          error,
        );


        await modal.alert(
          '가입된 아이디를 찾을 수 없습니다.',
        );

      } finally {
        setFinding(
          false,
        );
      }
    };


  /**
   * =========================================================
   * MM:SS
   * =========================================================
   */
  const formatTime = (
    seconds: number,
  ): string => {
    const minutes =
      Math.floor(
        seconds /
          60,
      );


    const remainSeconds =
      seconds %
      60;


    return `${String(
      minutes,
    ).padStart(
      2,
      '0',
    )}:${String(
      remainSeconds,
    ).padStart(
      2,
      '0',
    )}`;
  };


  return (
    <main
      className="
        min-h-screen
        bg-gray-50
        px-4
        py-10
      "
    >

      <div
        className="
          mx-auto
          w-full
          max-w-md
        "
      >

        {/* =================================================
            뒤로가기
            ================================================= */}
        <button
          type="button"
          onClick={() =>
            navigate(
              RouteConfig
                .Auth
                .Login
                .fullPath,
            )
          }
          className="
            mb-5
            flex
            items-center
            gap-2
            text-sm
            font-medium
            text-gray-500
            transition-colors
            hover:text-gray-900
          "
        >

          <ArrowLeft
            size={18}
          />

          로그인으로 돌아가기

        </button>


        <section
          className="
            rounded-2xl
            border
            border-gray-200
            bg-white
            p-7
            shadow-sm
          "
        >

          {/* =================================================
              Header
              ================================================= */}
          <header
            className="
              mb-8
              text-center
            "
          >

            <div
              className="
                mx-auto
                mb-4
                flex
                h-14
                w-14
                items-center
                justify-center
                rounded-full
                bg-gray-100
              "
            >

              <Smartphone
                size={26}
                className="
                  text-gray-700
                "
              />

            </div>


            <h1
              className="
                text-2xl
                font-semibold
                text-gray-900
              "
            >
              아이디 찾기
            </h1>


            <p
              className="
                mt-2
                text-sm
                leading-6
                text-gray-500
              "
            >
              회원가입 시 등록한
              전화번호를 인증해주세요.
            </p>

          </header>


          {foundUserId ? (

            /**
             * =============================================
             * 아이디 조회 완료
             * =============================================
             */
            <div>

              <div
                className="
                  rounded-xl
                  border
                  border-gray-200
                  bg-gray-50
                  px-5
                  py-7
                  text-center
                "
              >

                <CheckCircle2
                  size={42}
                  className="
                    mx-auto
                    text-emerald-500
                  "
                />


                <p
                  className="
                    mt-4
                    text-sm
                    text-gray-500
                  "
                >
                  회원님의 아이디
                </p>


                <div
                  className="
                    mt-2
                    break-all
                    text-2xl
                    font-semibold
                    text-gray-900
                  "
                >
                  {foundUserId}
                </div>

              </div>


              {/* 로그인 */}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    RouteConfig
                      .Auth
                      .Login
                      .fullPath,
                  )
                }
                className="
                  mt-5
                  min-h-12
                  w-full
                  rounded-xl
                  bg-main
                  px-4
                  py-3
                  text-base
                  font-semibold
                  text-white
                  transition-colors
                  hover:bg-main-medium
                "
              >
                로그인하기
              </button>


              {/* 비밀번호 찾기 */}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    RouteConfig
                      .Auth
                      .ResetPassword
                      .fullPath,
                  )
                }
                className="
                  mt-3
                  min-h-12
                  w-full
                  rounded-xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  py-3
                  text-base
                  font-medium
                  text-gray-700
                  transition-colors
                  hover:bg-gray-50
                "
              >
                비밀번호 찾기
              </button>

            </div>

          ) : (

            /**
             * =============================================
             * 전화번호 인증
             * =============================================
             */
            <div
              className="
                space-y-5
              "
            >

              {/* =================================================
                  전화번호
                  ================================================= */}
              <div>

                <label
                  htmlFor="phoneNumber"
                  className="
                    mb-2
                    block
                    text-sm
                    font-semibold
                    text-gray-700
                  "
                >
                  전화번호
                </label>


                <div
                  className="
                    flex
                    gap-2
                  "
                >

                  <input
                    id="phoneNumber"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={
                      phoneNumber
                    }
                    onChange={(
                      event,
                    ) =>
                      handlePhoneNumberChange(
                        event
                          .target
                          .value,
                      )
                    }
                    disabled={
                      phoneVerification
                        .phoneVerified
                    }
                    maxLength={11}
                    placeholder="01012345678"
                    className="
                      min-h-12
                      min-w-0
                      flex-1
                      rounded-xl
                      border
                      border-gray-300
                      bg-white
                      px-4
                      py-3
                      text-base
                      text-gray-900
                      outline-none
                      transition

                      placeholder:text-gray-400

                      focus:border-main
                      focus:ring-2
                      focus:ring-main/20

                      disabled:cursor-not-allowed
                      disabled:bg-gray-100
                      disabled:text-gray-500
                    "
                  />


                  <button
                    type="button"
                    onClick={() => {
                      void phoneVerification
                        .sendVerification();
                    }}
                    disabled={
                      phoneVerification
                        .isSendingVerification ||
                      phoneVerification
                        .phoneVerified ||
                      phoneVerification
                        .resendSeconds >
                        0
                    }
                    className="
                      min-h-12
                      shrink-0
                      whitespace-nowrap
                      rounded-xl
                      bg-gray-900
                      px-4
                      py-3
                      text-sm
                      font-semibold
                      text-white
                      transition

                      hover:bg-gray-700

                      disabled:cursor-not-allowed
                      disabled:opacity-50
                    "
                  >

                    {phoneVerification
                      .isSendingVerification
                      ? '발송 중'
                      : phoneVerification
                            .resendSeconds >
                          0
                        ? `${phoneVerification.resendSeconds}초`
                        : phoneVerification
                              .verificationSent
                          ? '재전송'
                          : '인증요청'}

                  </button>

                </div>

              </div>


              {/* =================================================
                  인증번호
                  ================================================= */}
              {phoneVerification
                .verificationSent && (

                <div>

                  <label
                    htmlFor="verificationCode"
                    className="
                      mb-2
                      block
                      text-sm
                      font-semibold
                      text-gray-700
                    "
                  >
                    인증번호
                  </label>


                  {phoneVerification
                    .phoneVerified ? (

                    <div
                      className="
                        flex
                        min-h-12
                        items-center
                        gap-2
                        rounded-xl
                        border
                        border-emerald-200
                        bg-emerald-50
                        px-4
                        py-3
                        text-sm
                        font-semibold
                        text-emerald-700
                      "
                    >

                      <CheckCircle2
                        size={18}
                      />

                      휴대폰 인증이 완료되었습니다.

                    </div>

                  ) : (

                    <>

                      <div
                        className="
                          relative
                        "
                      >

                        <input
                          id="verificationCode"
                          type="text"
                          inputMode="numeric"
                          value={
                            phoneVerification
                              .verificationCode
                          }
                          onChange={(
                            event,
                          ) =>
                            phoneVerification
                              .updateVerificationCode(
                                event
                                  .target
                                  .value,
                              )
                          }
                          maxLength={6}
                          placeholder="6자리 인증번호"
                          className="
                            min-h-12
                            w-full
                            rounded-xl
                            border
                            border-gray-300
                            bg-white
                            px-4
                            py-3
                            pr-20
                            text-base
                            text-gray-900
                            outline-none
                            transition

                            placeholder:text-gray-400

                            focus:border-main
                            focus:ring-2
                            focus:ring-main/20
                          "
                        />


                        <span
                          className="
                            absolute
                            right-4
                            top-1/2
                            -translate-y-1/2
                            text-sm
                            font-semibold
                            text-red-500
                          "
                        >
                          {formatTime(
                            phoneVerification
                              .verificationSeconds,
                          )}
                        </span>

                      </div>


                      <button
                        type="button"
                        onClick={() => {
                          void phoneVerification
                            .verifyCode();
                        }}
                        disabled={
                          phoneVerification
                            .isVerifyingCode ||
                          phoneVerification
                            .verificationSeconds <=
                            0
                        }
                        className="
                          mt-3
                          min-h-12
                          w-full
                          rounded-xl
                          border
                          border-gray-300
                          bg-white
                          px-4
                          py-3
                          text-sm
                          font-semibold
                          text-gray-700
                          transition

                          hover:bg-gray-50

                          disabled:cursor-not-allowed
                          disabled:opacity-50
                        "
                      >

                        {phoneVerification
                          .isVerifyingCode
                          ? '확인 중...'
                          : '인증번호 확인'}

                      </button>

                    </>

                  )}

                </div>

              )}


              {/* =================================================
                  인증 관련 메시지
                  ================================================= */}
              {phoneVerification
                .message && (

                <div
                  className={`
                    rounded-lg
                    px-4
                    py-3
                    text-sm

                    ${
                      phoneVerification
                        .phoneVerified
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-gray-50 text-gray-600'
                    }
                  `}
                >
                  {
                    phoneVerification
                      .message
                  }
                </div>

              )}


              {/* =================================================
                  아이디 찾기
                  ================================================= */}
              <button
                type="button"
                onClick={() => {
                  void handleFindId();
                }}
                disabled={
                  !phoneVerification
                    .phoneVerified ||
                  !phoneVerification
                    .verificationToken ||
                  finding
                }
                className="
                  flex
                  min-h-12
                  w-full
                  items-center
                  justify-center
                  rounded-xl
                  bg-main
                  px-4
                  py-3
                  text-base
                  font-semibold
                  text-white
                  transition

                  hover:bg-main-medium

                  disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >

                {finding
                  ? '조회 중...'
                  : '아이디 찾기'}

              </button>

            </div>

          )}

        </section>

      </div>

    </main>
  );
};


export default FindIdPage;